import { type RefObject, useEffect } from 'react';
import type { AskAllTrailsArgs } from '@/utils/askAllTrailsApi';
import { useAppDispatch } from '@/utils/redux';
import { setActiveAskTurn } from '@/utils/slices/askSlice';

// Long enough that scrolling past a turn doesn't flash its pins onto the map
const settleDelay = 150;

const thresholds = Array.from({ length: 21 }, (_, index) => index / 20);

/**
 * Makes the chat turn the user is reading the active one, so the map shows its answer, once scrolling settles: the newest turn
 * while any of it is in view, even when the answer before it covers more, otherwise whichever turn covers the most of the
 * transcript. `transcriptRef` holds one child per turn, in order.
 */
const useActiveAskTurn = (transcriptRef: RefObject<HTMLElement | null>, turns: AskAllTrailsArgs[]) => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    const transcript = transcriptRef.current;

    if (!transcript) {
      return undefined;
    }

    const visibleHeights = new Map<Element, number>();
    let timeout: ReturnType<typeof setTimeout> | undefined;

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => visibleHeights.set(entry.target, entry.isIntersecting ? entry.intersectionRect.height : 0));

        clearTimeout(timeout);
        timeout = setTimeout(() => {
          const heights = Array.from(transcript.children, element => visibleHeights.get(element) ?? 0);
          const mostVisibleHeight = Math.max(0, ...heights);
          const latestIndex = heights.length - 1;
          let activeIndex = -1;

          if (heights[latestIndex] > 0) {
            activeIndex = latestIndex;
          } else if (mostVisibleHeight > 0) {
            activeIndex = heights.indexOf(mostVisibleHeight);
          }

          const turn = turns[activeIndex];

          if (turn) {
            dispatch(setActiveAskTurn(turn.requestId));
          }
        }, settleDelay);
      },
      { threshold: thresholds }
    );

    Array.from(transcript.children).forEach(element => observer.observe(element));

    return () => {
      clearTimeout(timeout);
      observer.disconnect();
    };
  }, [dispatch, transcriptRef, turns]);
};

export default useActiveAskTurn;
